import { AlertRuleTier, TierSeverity } from '../../types';

/**
 * Independent tiers each measure a different series (one per scanner severity,
 * say), so their thresholds are not comparable to one another and the banded
 * ordering rule does not apply. Negative-threshold checks still do.
 */
export function computeTierErrors(
  tiers: AlertRuleTier[],
  operator: string,
  independentTiers = false
): Partial<Record<TierSeverity, string>> {
  const { critical, warning, info } = Object.fromEntries(
    tiers.map((t) => [t.severity, t])
  ) as Record<TierSeverity, AlertRuleTier>;
  const errors: Partial<Record<TierSeverity, string>> = {};

  if (critical?.enabled && critical.threshold < 0) {
    errors.critical = 'Threshold must be 0 or greater';
  }
  if (warning?.enabled && warning.threshold < 0) {
    errors.warning = 'Threshold must be 0 or greater';
  }
  if (info?.enabled && info.threshold < 0) {
    errors.info = 'Threshold must be 0 or greater';
  }

  if (independentTiers) {
    return errors;
  }

  const cmp =
    operator === '>'
      ? (a: number, b: number) => a > b
      : (a: number, b: number) => a < b;
  const ord = operator === '>' ? 'greater' : 'less';

  if (
    critical?.enabled &&
    warning?.enabled &&
    !cmp(critical.threshold, warning.threshold)
  ) {
    errors.critical = `Must be ${ord} than warning threshold`;
  }

  if (
    warning?.enabled &&
    info?.enabled &&
    !cmp(warning.threshold, info.threshold)
  ) {
    errors.warning = `Must be ${ord} than info threshold`;
  }

  // Only needed when warning is disabled; otherwise the critical->warning->info
  // ordering is transitive.
  if (
    critical?.enabled &&
    !warning?.enabled &&
    info?.enabled &&
    !cmp(critical.threshold, info.threshold)
  ) {
    errors.critical = `Must be ${ord} than info threshold`;
  }

  return errors;
}
